// RSS Feed Parser for True Hauntings Podcast
// Source: https://feeds.acast.com/public/shows/5f8e56cfe2dbd533ea999829

export interface Episode {
  slug: string;
  title: string;
  description: string;
  audioUrl: string;
  artwork: string;
  publishDate: string;
  duration: string;
  keywords: string[];
  episodeNumber?: number;
  guid: string;
}

const RSS_URL = 'https://feeds.acast.com/public/shows/5f8e56cfe2dbd533ea999829';

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim()
    .replace(/^-|-$/g, '');
}

function slugifyKeyword(keyword: string): string {
  return keyword
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function extractKeywords(item: Element): string[] {
  const keywordsEl = item.querySelector('keywords');
  const keywords: string[] = [];

  if (keywordsEl?.textContent) {
    const raw = keywordsEl.textContent;
    const parts = raw.includes(',') ? raw.split(',') : raw.split(' ');
    parts.forEach((k) => {
      const trimmed = k.trim();
      if (trimmed.length > 2) keywords.push(trimmed);
    });
  }

  // Also extract hashtags from description
  const descEl = item.querySelector('description');
  if (descEl?.textContent) {
    const hashtagMatches = descEl.textContent.match(/#([A-Za-z][A-Za-z0-9]+)/g) || [];
    hashtagMatches.forEach((tag) => {
      const word = tag.slice(1);
      if (!keywords.includes(word)) keywords.push(word);
    });
  }

  return keywords.slice(0, 20); // cap at 20 keywords per episode
}

function parseEpisode(item: Element, index: number): Episode {
  const getTag = (tag: string) => item.querySelector(tag)?.textContent?.trim() || '';
  const getAttr = (tag: string, attr: string) => item.querySelector(tag)?.getAttribute(attr) || '';

  const title = getTag('title');
  const description = getTag('description') || getTag('summary') || '';
  const audioUrl = getAttr('enclosure', 'url') || '';
  const guid = getTag('guid') || `episode-${index}`;
  const pubDate = getTag('pubDate');
  const duration = getTag('duration');
  const episodeNum = parseInt(getTag('episode') || '0', 10) || undefined;

  // Artwork: episode image > channel image fallback
  const imageEl = item.querySelector('image');
  const artwork = imageEl?.getAttribute('href') || imageEl?.textContent?.trim() || '';

  const keywords = extractKeywords(item);

  const slug = slugify(title) || `episode-${index}`;

  return {
    slug,
    title,
    description,
    audioUrl,
    artwork,
    publishDate: pubDate,
    duration,
    keywords,
    episodeNumber: episodeNum,
    guid,
  };
}

export async function fetchEpisodes(): Promise<Episode[]> {
  try {
    const res = await fetch(RSS_URL, {
      next: { revalidate: 3600 },
      headers: {
        'User-Agent': 'TrueHauntings-PodcastHub/1.0',
      },
    });

    if (!res.ok) {
      console.error('RSS fetch failed:', res.status);
      return [];
    }

    const xml = await res.text();

    if (!xml || xml.trim().length === 0) {
      console.error('RSS feed returned empty response');
      return [];
    }

    if (!xml.includes('<item>')) {
      console.error('RSS feed response contains no items');
      return [];
    }

    // Parse XML in Node.js environment using regex-based extraction
    const episodes = parseXML(xml);
    return episodes;
  } catch (err) {
    console.error('RSS parse error:', err);
    return [];
  }
}

function parseXML(xml: string): Episode[] {
  const episodes: Episode[] = [];

  // Extract channel-level artwork
  const channelImageMatch = xml.match(/<itunes:image[^>]*href="([^"]+)"/);
  const channelArtwork = channelImageMatch?.[1] || '';

  // Split into items
  const itemRegex = /<item>([\s\S]*?)<\/item>/g;
  let match;
  let index = 0;

  while ((match = itemRegex.exec(xml)) !== null) {
    const itemXml = match[1];
    index++;

    const getTag = (tag: string): string => {
      const m = itemXml.match(new RegExp(`<${tag}[^>]*><!\\[CDATA\\[([\\s\\S]*?)\\]\\]><\\/${tag}>|<${tag}[^>]*>([\\s\\S]*?)<\\/${tag}>`, 'i'));
      return (m?.[1] || m?.[2] || '').trim();
    };

    const getAttr = (tag: string, attr: string): string => {
      const m = itemXml.match(new RegExp(`<${tag}[^>]*${attr}="([^"]*)"`, 'i'));
      return m?.[1] || '';
    };

    const title = getTag('title');
    if (!title) continue;

    const description = getTag('description') || getTag('itunes:summary') || '';
    const audioUrl = getAttr('enclosure', 'url') || '';
    const guid = getTag('guid') || `episode-${index}`;
    const pubDate = getTag('pubDate');
    const duration = getTag('itunes:duration');
    const episodeNum = parseInt(getTag('itunes:episode') || '0', 10) || undefined;

    // Episode artwork
    const epImageMatch = itemXml.match(/<itunes:image[^>]*href="([^"]+)"/);
    const artwork = epImageMatch?.[1] || channelArtwork;

    // Keywords
    const keywordsRaw = getTag('itunes:keywords');
    const keywords: string[] = [];
    if (keywordsRaw) {
      const parts = keywordsRaw.includes(',') ? keywordsRaw.split(',') : keywordsRaw.split(' ');
      parts.forEach((k) => {
        const trimmed = k.trim();
        if (trimmed.length > 2) keywords.push(trimmed);
      });
    }

    // Extract hashtags from description
    const hashtagMatches = description.match(/#([A-Za-z][A-Za-z0-9]+)/g) || [];
    hashtagMatches.forEach((tag) => {
      const word = tag.slice(1);
      if (!keywords.includes(word) && keywords.length < 20) keywords.push(word);
    });

    const slug = slugify(title) || `episode-${index}`;

    episodes.push({
      slug,
      title,
      description,
      audioUrl,
      artwork,
      publishDate: pubDate,
      duration,
      keywords,
      episodeNumber: episodeNum,
      guid,
    });
  }

  return episodes;
}

export function getAllKeywords(episodes: Episode[]): string[] {
  const keywordSet = new Set<string>();
  episodes.forEach((ep) => {
    ep.keywords.forEach((k) => keywordSet.add(k));
  });
  return Array.from(keywordSet).sort();
}

export function getEpisodesByKeyword(episodes: Episode[], keyword: string): Episode[] {
  const normalised = keyword.toLowerCase().replace(/-/g, ' ');
  return episodes.filter((ep) =>
    ep.keywords.some((k) => k.toLowerCase().replace(/-/g, ' ') === normalised)
  );
}

export function getEpisodeBySlug(episodes: Episode[], slug: string): Episode | undefined {
  return episodes.find((ep) => ep.slug === slug);
}

export function slugifyKeywordForUrl(keyword: string): string {
  return slugifyKeyword(keyword);
}

export function formatKeywordDisplay(keyword: string): string {
  // Remove leading # if present
  const stripped = keyword.startsWith('#') ? keyword.slice(1) : keyword;
  // Split PascalCase / camelCase into words: insert space before each uppercase letter
  // that follows a lowercase letter or another uppercase letter followed by lowercase
  return stripped
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim();
}

export function formatDuration(duration: string): string {
  if (!duration) return '';
  // Already formatted HH:MM:SS
  if (duration.includes(':')) return duration;
  // Seconds
  const secs = parseInt(duration, 10);
  if (isNaN(secs)) return duration;
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  } catch {
    return dateStr;
  }
}
